import { useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useToast } from 'components/hooks/useToast';
import { isEmpty } from 'lodash';

import { ALERT_MESSAGES, TOAST_STATUSES } from 'constants/ProjectConstants';
import { sendSharedFeedback } from 'constants/mixpanelHelper';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { getApiBodyFromEditorStateAndMedia } from 'services/helpers/post';

import shareFeedbackNew from '../services';
import { useProvidedFeedbackData } from '../slice/feedbackSlice';

export const useSaveShareFeedback = ({ source, onClose }: { source: string; onClose: () => void }) => {
  const toast = useToast();
  const {
    feedbackPost,
    feedbackSentimentType,
    goal = {},
    visibility,
    attachments,
    applicableEmployees,
  } = useProvidedFeedbackData();

  const [isSaving, setIsSaving] = useState(false);

  const queryClient = useQueryClient();

  const onSave = async () => {
    const text = feedbackPost.getCurrentContent().getPlainText('\n');
    if (text.length === 0) {
      toast({
        title: ALERT_MESSAGES.CONTINUOUS_FEEDBACK.SHARE.FAILURE,
        status: TOAST_STATUSES.ERROR,
        position: 'bottom-left',
      });
      return;
    }

    if (applicableEmployees.length === 0) {
      toast({
        title: ALERT_MESSAGES.CONTINUOUS_FEEDBACK.SHARE.FAILURE,
        status: TOAST_STATUSES.ERROR,
        position: 'bottom-left',
      });
      return;
    }

    setIsSaving(true);
    const data = getApiBodyFromEditorStateAndMedia(feedbackPost, null, false, feedbackSentimentType);

    const feedbackHasCompetency = !isEmpty(data?.macros?.attributes?.orgTag);

    sendSharedFeedback({
      eventProperties: {
        source,
        feedbackType: feedbackSentimentType === 'APPRECIATIVE' ? 'Praise' : 'Advice',
        visibility,
        association: goal,
        competency: feedbackHasCompetency,
      },
    });

    const media = [...(attachments?.images ?? []), ...(attachments?.gifs ?? [])];

    let macros = data?.macros;

    if (goal) {
      macros = {
        ...macros,
        demographics: {
          ...macros?.demographics,
          GOAL_TAG: {
            // @ts-expect-error Property 'uuid' does not exist on type {}
            [goal?.uuid]: {
              // @ts-expect-error Property 'uuid' does not exist on type {}
              uuid: goal?.uuid,
            },
          },
        },
      };
    }

    const body = {
      text: data?.text,
      macros,
      media,
      visibilityType: 'GLOBAL',
    };

    try {
      await shareFeedbackNew({
        body,
        feedbackSentimentType,
        visibility,
      });
      toast({
        title: ALERT_MESSAGES.CONTINUOUS_FEEDBACK.SHARE.SUCCESS,
        status: TOAST_STATUSES.SUCCESS,
        position: 'bottom-left',
      });
      onClose();
    } catch (error) {
      console.error('Error sharing feedback ', error);
      toast({
        title: ALERT_MESSAGES.CONTINUOUS_FEEDBACK.SHARE.FAILURE,
        status: TOAST_STATUSES.ERROR,
        position: 'bottom-left',
      });
    } finally {
      setIsSaving(false);
      queryClient.invalidateQueries({ queryKey: ['CFTable'] });
      queryClient.invalidateQueries({ queryKey: ['CFTabs'] });
      queryClient.invalidateQueries({ queryKey: [REACT_QUERY_KEYS.mySummary.mySummaryFeedback] });
    }
  };

  return {
    onSave,
    isSaving,
  };
};
